import { api } from '../../lib/apiClient';

/**
 * Multipart-upload rechtstreeks naar R2. De bytes gaan niet door de Worker: die
 * deelt alleen ondertekende URL's uit en sluit de upload af.
 *
 * Elk deel wordt los verstuurd zodat we voortgang kunnen tonen én zodat één
 * mislukt deel niet de hele opname opnieuw kost. Een opname van drie kwartier is
 * al gauw tientallen megabytes, en een buddy die na vijf minuten wachten
 * opnieuw moet beginnen upload de volgende keer niets meer.
 */

export interface UploadStart {
  key: string;
  uploadId: string;
  deelGrootte: number;
  urls: string[];
  /** Lokaal draaien zonder R2-sleutels: in één stuk door de Worker heen. */
  direct?: boolean;
}

/** Hoe vaak één deel opnieuw wordt geprobeerd voordat de upload opgeeft. */
const POGINGEN_PER_DEEL = 3;

async function stuurDeel(url: string, blok: Blob, poging = 1): Promise<string> {
  try {
    const res = await fetch(url, { method: 'PUT', body: blok });
    if (!res.ok) throw new Error(`Deel weigeren door opslag (${res.status})`);
    const etag = res.headers.get('ETag');
    // Zonder ETag kan de upload niet worden afgesloten. Dat is bijna altijd een
    // ontbrekende `ExposeHeaders` in de CORS-configuratie van de bucket — de
    // header is er wel, maar de browser laat ons er niet bij.
    if (!etag) throw new Error('Opslag gaf geen ETag terug; controleer de CORS-instelling');
    return etag;
  } catch (err) {
    if (poging >= POGINGEN_PER_DEEL) throw err;
    await new Promise((r) => setTimeout(r, 500 * poging));
    return stuurDeel(url, blok, poging + 1);
  }
}

export async function uploadBestand(
  meetingId: string,
  bestand: File,
  onVoortgang: (fractie: number) => void,
): Promise<void> {
  const start = await api.post<UploadStart>(`/meetings/${meetingId}/upload/start`, {
    naam: bestand.name,
    grootte: bestand.size,
    type: bestand.type,
  });

  // Lokaal is er geen R2 om naartoe te tekenen; dan gaat het bestand in één
  // stuk door de Worker naar de R2-binding op schijf.
  if (start.direct) {
    const res = await fetch(
      `/api/meetings/${meetingId}/upload/direct?key=${encodeURIComponent(start.key)}`,
      {
        method: 'PUT',
        body: bestand,
        headers: { 'content-type': bestand.type || 'application/octet-stream' },
        credentials: 'same-origin',
      },
    );
    if (!res.ok) throw new Error(`Upload mislukt (${res.status})`);
    onVoortgang(1);
    await api.post(`/meetings/${meetingId}/upload/voltooi`, {
      key: start.key,
      uploadId: start.uploadId,
      etags: [],
    });
    return;
  }

  const etags: string[] = [];
  try {
    for (const [i, url] of start.urls.entries()) {
      const van = i * start.deelGrootte;
      const blok = bestand.slice(van, Math.min(van + start.deelGrootte, bestand.size));
      etags.push(await stuurDeel(url, blok));
      onVoortgang((i + 1) / start.urls.length);
    }
  } catch (err) {
    // De halve upload netjes afsluiten, anders blijven de al verstuurde delen in
    // R2 staan. De nachtelijke taak ruimt ze anders pas na een dag op.
    await api
      .post(`/meetings/${meetingId}/upload/afbreken`, {
        key: start.key,
        uploadId: start.uploadId,
      })
      .catch(() => undefined);
    throw err;
  }

  await api.post(`/meetings/${meetingId}/upload/voltooi`, {
    key: start.key,
    uploadId: start.uploadId,
    etags,
  });
}
